import React from 'react';

// Object kinds of a profiled source (GRAPH_API v3, `attributes.objectKind`). A Salesforce profile
// holds every queryable object, each with its kind; a node without one — every relational table
// and Mongo collection — is DATA. Lists and pickers show EVERY kind until the user turns one off:
// hiding kinds by default kept objects people must pick (RecordType, Skill, …) out of the lists.
export const OBJECT_KINDS = [
  ['DATA', 'Data'],
  ['CONFIGURATION', 'Configuration'],
  ['SETUP', 'Setup'],
  ['DERIVED', 'Share / History / Feed'],
  ['METADATA', 'Custom settings / metadata'],
  ['EXTERNAL', 'External'],
  ['BIG', 'Big objects'],
];

const LABELS = Object.fromEntries(OBJECT_KINDS);

export const objectKindOf = (node) => {
  const k = node && node.attributes ? node.attributes.objectKind : null;
  return k ? String(k).toUpperCase() : 'DATA';
};

export const objectKindLabel = (kind) => LABELS[kind] || kind;

/** { kind: count } over `items`. */
export const kindCounts = (items) => {
  const out = {};
  (items || []).forEach((n) => { const k = objectKindOf(n); out[k] = (out[k] || 0) + 1; });
  return out;
};

/** `items` without the kinds in `hidden` (a Set); every item when nothing is hidden. */
export const withoutHiddenKinds = (items, hidden) => {
  if (!hidden || hidden.size === 0) return items || [];
  return (items || []).filter((n) => !hidden.has(objectKindOf(n)));
};

/**
 * One toggle per kind present in `items`, each with its count, all on until turned off.
 * `hidden` (a Set of kinds) / `onChange(nextSet)`. Renders nothing when the items hold a single
 * kind (a relational source), so it can be dropped next to any object list or picker.
 */
export const KindToggles = ({ items, hidden, onChange, chipClass = 'dg-chip', testId = 'dg-kinds', disabled = false }) => {
  const counts = kindCounts(items);
  const present = OBJECT_KINDS.filter(([k]) => counts[k]);
  Object.keys(counts).forEach((k) => { if (!LABELS[k]) present.push([k, k]); });
  if (present.length < 2) return null;
  const off = hidden || new Set();
  const toggle = (k) => {
    const next = new Set(off);
    if (next.has(k)) next.delete(k); else next.add(k);
    onChange(next);
  };
  return (
    <span className={`${chipClass}s dg-kinds`} data-testid={testId} role='group' aria-label='Object kinds'>
      {present.map(([k, label]) => (
        <button key={k} type='button' className={`${chipClass}${off.has(k) ? '' : ` ${chipClass}--on`}`} disabled={disabled}
          aria-pressed={!off.has(k)} onClick={() => toggle(k)} data-testid={`${testId}-${k}`}>
          {label} ({counts[k]})
        </button>
      ))}
    </span>
  );
};
